import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SiSlack, SiDiscord, SiWhatsapp, SiGmail, SiTelegram } from "react-icons/si";
import Analytics from '../../services/analytics';

// Import platform interfaces
import SlackInterface from '../platforms/SlackInterface';
import DiscordInterface from '../platforms/DiscordInterface';
import WhatsAppInterface from '../platforms/WhatsAppInterface';
import GmailInterface from '../platforms/GmailInterface';
import TelegramInterface from '../platforms/TelegramInterface';

// Define the possible platforms
type Platform = 'slack' | 'discord' | 'whatsapp' | 'gmail' | 'telegram';

const LiveDemoSectionV2: React.FC = () => {
  const [activePlatform, setActivePlatform] = useState<Platform>('slack');
  // We need this state for tracking messages sent by the user
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [userMessage, setUserMessage] = useState('');
  
  // Listen for platform switch events
  useEffect(() => {
    const handlePlatformSwitch = (event: CustomEvent) => {
      const { platform } = event.detail;
      if (platform && typeof platform === 'string') {
        setActivePlatform(platform as Platform);
      }
    };
    
    // Add event listener
    window.addEventListener('switchPlatform', handlePlatformSwitch as EventListener);
    
    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener('switchPlatform', handlePlatformSwitch as EventListener);
    };
  }, []);

  // Platform data for tabs and descriptions
  const platforms = [
    { 
      id: 'slack', 
      name: 'Slack', 
      description: 'Your AI assistant within Slack workspaces - access all tools without leaving your channel',
      color: 'bg-[#3F0E40]',
      textColor: 'text-white',
      icon: SiSlack({ className: "w-5 h-5" })
    },
    { 
      id: 'discord', 
      name: 'Discord', 
      description: 'Your AI assistant on Discord servers - help with moderation, searches, and accessing tools',
      color: 'bg-[#5865F2]',
      textColor: 'text-white',
      icon: SiDiscord({ className: "w-5 h-5" })
    },
    { 
      id: 'whatsapp', 
      name: 'WhatsApp', 
      description: 'Your AI assistant on WhatsApp - chat naturally and complete tasks from your messaging app',
      color: 'bg-[#25D366]',
      textColor: 'text-white',
      icon: SiWhatsapp({ className: "w-5 h-5" })
    },
    { 
      id: 'gmail', 
      name: 'Gmail', 
      description: 'Your AI assistant via email - compose, analyze, and extract information right from your inbox',
      color: 'bg-red-100',
      textColor: 'text-gray-800',
      icon: SiGmail({ className: "w-5 h-5", color: "#EA4335" })
    },
    { 
      id: 'telegram', 
      name: 'Telegram', 
      description: 'Your AI assistant on Telegram - chat, search, and organize information from any device',
      color: 'bg-[#2AABEE]',
      textColor: 'text-white',
      icon: SiTelegram({ className: "w-5 h-5" })
    }
  ];

  // Handle sending a user message
  const handleSendMessage = (message: string) => {
    // Store the message for potential future use
    setUserMessage(message);
    
    // Track message sent event
    Analytics.trackMessageSent(activePlatform, message.length);
    
    // In a real implementation, this would connect to an API
    console.log(`Message sent via ${activePlatform}: ${message} (stored in userMessage state)`);
  };

  // Animation variants for the platform container
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    },
    exit: { 
      opacity: 0,
      y: -20,
      transition: { duration: 0.3 }
    }
  };

  return (
    <section className="pt-24 pb-16 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Platform selection tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {platforms.map((platform) => (
            <button
              key={platform.id}
              onClick={() => {
                const newPlatform = platform.id as Platform;
                setActivePlatform(newPlatform);
                Analytics.trackPlatformSelection(newPlatform);
              }}
              className={`flex items-center px-4 py-2 rounded-full transition-all duration-200 ${
                activePlatform === platform.id 
                  ? `${platform.color} ${platform.textColor} shadow-md`
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="mr-2">{platform.icon}</span>
              {platform.name}
            </button>
          ))}
        </div>

        {/* Platform interface display */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activePlatform}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="mb-16"
          >
            {activePlatform === 'slack' && (
              <SlackInterface onSendMessage={handleSendMessage} />
            )}
            {activePlatform === 'discord' && (
              <DiscordInterface onSendMessage={handleSendMessage} />
            )}
            {activePlatform === 'whatsapp' && (
              <WhatsAppInterface onSendMessage={handleSendMessage} />
            )}
            {activePlatform === 'gmail' && (
              <GmailInterface onSendMessage={handleSendMessage} />
            )}
            {activePlatform === 'telegram' && (
              <TelegramInterface onSendMessage={handleSendMessage} />
            )}
          </motion.div>
        </AnimatePresence>
        
        {/* Section heading and description moved below demos */}
        <div className="max-w-4xl mx-auto text-center mb-8 mt-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Experience Assistants in Their Native Environments
          </h2>
          <p className="text-xl text-gray-600">
            Our AI assistants integrate seamlessly with the platforms your team already uses.
            No need to switch context - get powerful AI help right where you work.
          </p>
        </div>

        {/* Platform description moved below demos */}
        <div className="max-w-3xl mx-auto text-center mb-8">
          <p className="text-gray-600">
            {platforms.find(p => p.id === activePlatform)?.description}
          </p>
        </div>

        {/* Features section */}
        <div className="max-w-5xl mx-auto mt-16">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Native Integration</h3>
              <p className="text-gray-600">
                Our assistants live inside the platforms you already use,
                with interfaces that match the native experience perfectly.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Cross-platform Tools</h3>
              <p className="text-gray-600">
                No matter which platform you're using, our assistants can access
                the same powerful set of tools to get your work done.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Seamless Experience</h3>
              <p className="text-gray-600">
                Your team doesn't need to learn a new interface or switch
                between apps to harness the power of AI assistants.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LiveDemoSectionV2;